<template>
  <v-container id="timeline-label-enumerator" class="d-flex flex-column">
    <v-data-table-with-auto-scroll
      id="timeline-label-table"
      ref="timelineLabelTable"
      class="mt-1 d-flex flex-column"
      dense hide-default-footer sort-by="beginMsec" fixed-header
      v-bind:auto-scroll.sync="$data.$_enablesAutoScroll"
      v-bind:headers="$_timelineSegmentHeaders"
      v-bind:items="$_allSelectedTimelineSegmentItems"
      v-bind:items-per-page="$_allSelectedTimelineSegmentItems.length"
      v-bind:search="$data.$_filterKeyword"
      v-bind:targetIdcs="$_scrollTargetIdcs"
      v-on:click:row="$_onClickRow"
      v-on:current-items="$_onCurrentItems"
    >
      <template v-slot:top>
        <div class="d-flex flex-row align-end mb-1">
          <v-switch
            dense hide-details
            label="Auto Scroll"
            class="mr-3"
            v-model="$data.$_enablesAutoScroll"
            v-on:keydown.stop
          >
          </v-switch>
          <v-select
            dense multiple hide-details
            label="Timeline Data ID"
            class="mr-3"
            v-model="$data.$_currentTimelineDataIds"
            v-bind:items="$_timelineDataIdAndNames"
            v-on:keydown.stop
          >
          </v-select>
          <v-text-field
            dense hide-details
            label="Filter"
            v-model="$data.$_filterKeyword"
          >
          </v-text-field>
        </div>
      </template>

      <template v-slot:item.beginMsec="{ item }">
        {{ item.beginString }}
      </template>

      <template v-slot:item.endMsec="{ item }">
        {{ item.endString }}
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="d-flex flex-nowrap">
          <v-btn
            icon small class="flex-nowrap"
            v-on:click.stop="$_openDialog(item)"
          >
            <v-icon small>mdi-dock-window</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table-with-auto-scroll>
  </v-container>
</template>

<style scoped>
#timeline-label-enumerator,
#timeline-label-table {
  height: 100%;
}
</style>

<script>
import VDataTableWithAutoScroll from '../Parts/VDataTableWithAutoScroll.vue';
import { TimelineDataSet, TimelineSegmentId } from '../../modules/ScattData.js';
import LabelSegmentData from '../../modules/segmentDataTypes/LabelSegmentData.js';
import EditorDefinition from '../../modules/EditorDefinition.js';

export const TimelineLabelEnumeratorDefinition = new EditorDefinition(
  'ScattTimelineLabelEnumerator',
  'Transcription',
  timelineDataSet => {
    for (let timelineData of Object.values(timelineDataSet)) {
      if (timelineData.segmentDataType === LabelSegmentData) return true;
    }
    return false;
  },
);

export default {
  components: {
    VDataTableWithAutoScroll,
  },

  props: {
    timelineDataSet: { type: TimelineDataSet },
  },

  watch: {
    '$data.$_lastPlayTimeMsec'(lastPlayTimeMsec) {
      this.$_updateTableState(this.$data.$_filteredTimelineSegmentItems, lastPlayTimeMsec);
    },

    $_allSelectedTimelineSegmentItems() { this.$_resetScrollTarget() },
  },

  data() {
    return {
      $_lastPlayTimeMsec: 0,
      $_onPlayInfoUpdatedCallbackId: undefined,
      $_enablesAutoScroll: true,
      $_currentTimelineDataIds: [],
      $_filterKeyword: '',
      $_scrollTargetFirstRowIdx: undefined,
      $_scrollTargetLastRowIdx: undefined,
      $_markedRowIdcs: [],
      $_filteredTimelineSegmentItems: [],
    };
  },

  computed: {
    $_timelineDataIdAndNames() {
      let timelineDataIdAndNames = [];
      for (let [ timelineDataId, timelineData ] of Object.entries(this.timelineDataSet)) {
        if (timelineData.segmentDataType === LabelSegmentData) {
          timelineDataIdAndNames.push({
            text: `[${timelineDataId}] ${timelineData.name}`,
            value: timelineDataId,
          });
        }
      }
      return timelineDataIdAndNames;
    },

    $_timelineSegmentHeaders() {
      return [
        { value: 'beginMsec',        text: 'Begin',    sortable: false },
        { value: 'endMsec',          text: 'End',      sortable: false },
        { value: 'timelineDataName', text: 'Timeline', sortable: false },
        { value: 'label',            text: 'Label',    sortable: false },
        { value: 'actions',                            sortable: false },
      ];
    },

    $_allSelectedTimelineSegmentItems() {
      if (!this.$data.$_currentTimelineDataIds) return [];
      let timelineSegmentItems = [];
      for (let timelineDataId of this.$data.$_currentTimelineDataIds) {
        let timelineData = this.timelineDataSet[timelineDataId];
        for (let [ segmentId, segment ] of Object.entries(timelineData.segments)) {
          let timelineSegmentItem = {
            beginMsec: (segment.begin === null)? -Infinity : segment.begin.msec,
            beginString: (segment.begin === null)? '' : segment.begin.toFormatString(),
            endMsec: (segment.end === null)? Infinity : segment.end.msec,
            endString: (segment.end === null)? '' : segment.end.toFormatString(),
            timelineDataName: timelineData.name,
            label: segment.data,
            timelineSegmentId: new TimelineSegmentId(timelineDataId, segmentId),
          };
          let insertIdx = timelineSegmentItems.findIndex(
            existingTimelineSegmentItem => timelineSegmentItem.beginMsec < existingTimelineSegmentItem.beginMsec
          );
          if (insertIdx === -1) {
            timelineSegmentItems.push(timelineSegmentItem);
          } else {
            timelineSegmentItems.splice(insertIdx, 0, timelineSegmentItem);
          }
        }
      }
      return timelineSegmentItems;
    },

    $_scrollTargetIdcs() {
      if (this.$data.$_scrollTargetFirstRowIdx === undefined) return undefined;
      if (this.$data.$_scrollTargetLastRowIdx === undefined) return undefined;
      if (this.$data.$_scrollTargetFirstRowIdx === this.$data.$_scrollTargetLastRowIdx) return [ this.$data.$_scrollTargetFirstRowIdx ];
      return [ this.$data.$_scrollTargetFirstRowIdx, this.$data.$_scrollTargetLastRowIdx ];
    },
  },

  inject: [
    'registerOnPlayInfoUpdatedCallback',
    'unregisterOnPlayInfoUpdatedCallback',
    'invokeActionOnSelectedTimelineSegments',
    'selectTimelineSegments',
    'clearSelectedTimelineSegments',
    'selectTimelineSegmentAndSeek',
  ],

  created() {
    this.$data.$_onPlayInfoUpdatedCallbackId = this.registerOnPlayInfoUpdatedCallback(this.$_onPlayInfoUpdatedCallback);
    this.$data.$_currentTimelineDataIds = this.$_timelineDataIdAndNames.map(
      timelineDataIdAndName => { return timelineDataIdAndName.value },
    );
    this.$data.$_filteredTimelineSegmentItems = this.$_allSelectedTimelineSegmentItems;
  },

  beforeDestroy() {
    this.unregisterOnPlayInfoUpdatedCallback(this.$data.$_onPlayInfoUpdatedCallbackId);
  },

  methods: {
    /* private */
    $_onClickRow(event) { this.selectTimelineSegmentAndSeek(event.timelineSegmentId) },

    $_onPlayInfoUpdatedCallback(playInfo) {
      if (playInfo.playTime.msec < this.$data.$_lastPlayTimeMsec) {
        this.$_resetScrollTarget();
      }
      this.$data.$_lastPlayTimeMsec = playInfo.playTime.msec;
    },

    $_openDialog(event) {
      this.clearSelectedTimelineSegments();
      this.selectTimelineSegments(event.timelineSegmentId);
      this.invokeActionOnSelectedTimelineSegments('editSegment');
    },

    $_resetScrollTarget() {
      this.$data.$_scrollTargetFirstRowIdx = undefined;
      this.$data.$_scrollTargetLastRowIdx = undefined;
    },

    $_updateTableState(timelineSegmentItems, playTimeMsec) {
      updateScrollTarget.call(this, timelineSegmentItems, playTimeMsec);
      updateTableRowStyle.call(this, timelineSegmentItems, playTimeMsec);

      function updateScrollTarget(timelineSegmentItems, playTimeMsec) {
        let previousBeginMsec = -Infinity;
        let previousItemIdx = undefined;
        let itemIdx = this.$data.$_scrollTargetFirstRowIdx;
        if (itemIdx === undefined) itemIdx = 0;
        for (; itemIdx < timelineSegmentItems.length; ++itemIdx) {
          let beginMsec = timelineSegmentItems[itemIdx].beginMsec;
          if (playTimeMsec <= beginMsec) break;
          if (previousBeginMsec !== beginMsec) {
            previousItemIdx = itemIdx;
          }
          previousBeginMsec = beginMsec;
        }
        this.$data.$_scrollTargetFirstRowIdx = previousItemIdx;
        this.$data.$_scrollTargetLastRowIdx = (previousItemIdx === undefined)? undefined : (itemIdx - 1)
      }

      function updateTableRowStyle(timelineSegmentItems, playTimeMsec) {
        let previousMarkedRowIdcs = this.$data.$_markedRowIdcs;
        let markedRowIdcs = [];
        for (let itemIdx = 0; itemIdx < timelineSegmentItems.length; ++itemIdx) {
          let beginMsec = timelineSegmentItems[itemIdx].beginMsec;
          let endMsec = timelineSegmentItems[itemIdx].endMsec;
          if ((beginMsec <= playTimeMsec) && (playTimeMsec < endMsec)) {
            markedRowIdcs.push(itemIdx);
          }
          if (playTimeMsec < beginMsec) break;
        }
        let unmarkedRowIdcs = [];
        let newlyMarkedRowIdcs = []
        for (let previousMarkedRowIdx of previousMarkedRowIdcs) {
          if (!markedRowIdcs.includes(previousMarkedRowIdx)) unmarkedRowIdcs.push(previousMarkedRowIdx)
        }
        for (let markedRowIdx of markedRowIdcs) {
          if (!previousMarkedRowIdcs.includes(markedRowIdx)) newlyMarkedRowIdcs.push(markedRowIdx)
        }
        for (let rowIdx of unmarkedRowIdcs) {
          let tableRowElement = this.$refs.timelineLabelTable.getTableRowElement(rowIdx);
          tableRowElement.classList.toggle('primary', false);
          tableRowElement.classList.toggle('lighten-4', false);
        }
        for (let rowIdx of newlyMarkedRowIdcs) {
          let tableRowElement = this.$refs.timelineLabelTable.getTableRowElement(rowIdx);
          tableRowElement.classList.toggle('primary', true);
          tableRowElement.classList.toggle('lighten-4', true);
        }
        this.$data.$_markedRowIdcs = markedRowIdcs;
      }
    },

    $_onCurrentItems(filteredTimelineSegmentItems) {
      this.$data.$_filteredTimelineSegmentItems = filteredTimelineSegmentItems;
      // wait DOM updated
      this.$nextTick(() => { this.$_updateTableState(filteredTimelineSegmentItems, this.$data.$_lastPlayTimeMsec) });
    },
  },
};
</script>